import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@/primitives';
import DynamicFilterRow, {
  type DynamicFilterRowData,
} from './DynamicFilterRow';
import type { FilterInput } from '../../constants';
import type { DynamicFieldOption } from '../constants';

let nextRowId = 1;
function createEmptyRow(): DynamicFilterRowData {
  return { id: String(nextRowId++), field: '', operator: '', value: '' };
}

interface Props {
  fieldOptions: DynamicFieldOption[];
  onChange: (filters: FilterInput[]) => void;
  initialFilters?: FilterInput[];
  resetKey?: number;
}

export default function DynamicFilterSection({
  fieldOptions,
  onChange,
  initialFilters,
  resetKey,
}: Props) {
  const [rows, setRows] = useState<DynamicFilterRowData[]>(() => {
    if (initialFilters && initialFilters.length > 0) {
      const hydrated = initialFilters.map((f) => ({
        id: String(nextRowId++),
        field: f.field,
        operator: f.operator,
        value: f.value,
      }));
      hydrated.push(createEmptyRow());
      return hydrated;
    }
    return [createEmptyRow()];
  });

  const prevResetKey = useRef(resetKey);
  useEffect(() => {
    if (resetKey !== prevResetKey.current) {
      prevResetKey.current = resetKey;
      const newRows = [createEmptyRow()];
      setRows(newRows);
      onChange([]);
    }
  }, [resetKey, onChange]);

  const prevInitialFiltersRef = useRef(initialFilters);
  useEffect(() => {
    if (initialFilters === prevInitialFiltersRef.current) return;
    prevInitialFiltersRef.current = initialFilters;

    if (!initialFilters || initialFilters.length === 0) {
      setRows([createEmptyRow()]);
      return;
    }

    const hydrated: DynamicFilterRowData[] = initialFilters.map((f) => ({
      id: String(nextRowId++),
      field: f.field,
      operator: f.operator,
      value: f.value,
    }));
    hydrated.push(createEmptyRow());
    setRows(hydrated);
  }, [initialFilters]);

  const emitChange = useCallback(
    (nextRows: DynamicFilterRowData[]) => {
      const filters: FilterInput[] = nextRows
        .filter((r) => r.field && r.operator)
        .map((r) => ({ field: r.field, operator: r.operator, value: r.value }));
      onChange(filters);
    },
    [onChange],
  );

  const handleUpdate = useCallback(
    (id: string, patch: Partial<Omit<DynamicFilterRowData, 'id'>>) => {
      setRows((prev) => {
        const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));

        const lastRow = next[next.length - 1];
        if (lastRow && (lastRow.field || lastRow.operator || lastRow.value)) {
          next.push(createEmptyRow());
        }

        emitChange(next);
        return next;
      });
    },
    [emitChange],
  );

  const handleDelete = useCallback(
    (id: string) => {
      setRows((prev) => {
        const next = prev.filter((r) => r.id !== id);
        if (next.length === 0) {
          next.push(createEmptyRow());
        }
        emitChange(next);
        return next;
      });
    },
    [emitChange],
  );

  const filledRowCount = rows.filter(
    (r) => r.field || r.operator || r.value,
  ).length;

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="labelmedium"
        sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}
      >
        Additional Filters
      </Typography>
      {rows.map((row) => (
        <DynamicFilterRow
          key={row.id}
          row={row}
          fieldOptions={fieldOptions}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          showDelete={filledRowCount > 1 || rows.length > 1}
        />
      ))}
    </Box>
  );
}
